import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  work:     { label: 'Jobb',       emoji: '💼', color: '#4A8BC7' },
  personal: { label: 'Personligt', emoji: '💗', color: '#D06892' },
  kids:     { label: 'Barn',       emoji: '🧸', color: '#E0A330' },
  training: { label: 'Träning',    emoji: '🏃', color: '#4DAA4D' },
  health:   { label: 'Hälsa',      emoji: '🏥', color: '#4DAA6B' },
  social:   { label: 'Socialt',    emoji: '🎉', color: '#D6B742' },
  study:    { label: 'Studier',    emoji: '📚', color: '#8B63C4' },
  other:    { label: 'Annat',      emoji: '🌸', color: '#888888' },
};

export default function CategoryMixCard() {
  const [events, setEvents] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const now = new Date();
        const start = new Date(now); start.setDate(now.getDate() - days);
        const res = await axios.get(`${API}/calendar/events`, {
          withCredentials: true,
          params: { from_date: start.toISOString(), to_date: now.toISOString() },
        });
        if (!cancelled) setEvents(res.data?.items || []);
      } catch {
        if (!cancelled) setEvents([]);
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [days]);

  const slices = useMemo(() => {
    const counts = {};
    events.forEach((e) => {
      const c = e.category || 'other';
      counts[c] = (counts[c] || 0) + 1;
    });
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    if (total === 0) return { items: [], total: 0 };
    const items = Object.entries(counts)
      .map(([k, v]) => ({ key: k, count: v, pct: v / total, meta: CATEGORY_META[k] || CATEGORY_META.other }))
      .sort((a, b) => b.count - a.count);
    return { items, total };
  }, [events]);

  // Build conic-gradient string
  const gradient = useMemo(() => {
    if (!slices.items.length) return 'conic-gradient(#E5E5E5 0 360deg)';
    let acc = 0;
    const stops = slices.items.map((s) => {
      const start = acc;
      acc += s.pct * 360;
      return `${s.meta.color} ${start}deg ${acc}deg`;
    });
    return `conic-gradient(${stops.join(', ')})`;
  }, [slices]);

  // Find dominant category (>50%)
  const dominant = slices.items[0];
  const dominantPct = Math.round((dominant?.pct || 0) * 100);
  const insight = useMemo(() => {
    if (!slices.total) return 'Inga händelser senaste perioden 🌸';
    if (dominantPct >= 60) {
      return `${dominant.meta.emoji} ${dominant.meta.label} dominerar (${dominantPct}%) — kom ihåg balans 💛`;
    }
    if (slices.items.length >= 4) {
      return '✨ Bra balans mellan flera områden';
    }
    return `Mest tid på ${dominant.meta.emoji} ${dominant.meta.label} (${dominantPct}%)`;
  }, [slices, dominant, dominantPct]);

  return (
    <section className="neo-card p-4 bg-gradient-to-br from-[#FFF7E6] via-[#FFF0F6] to-[#F5EEFF]" data-testid="category-mix-card">
      <header className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
        <div>
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase opacity-60">Kategorier</p>
          <h3 className="font-['Fredoka'] font-bold text-lg">Kategori-mix</h3>
        </div>
        <div className="flex gap-1" data-testid="category-mix-range">
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`text-[11px] font-bold px-2 py-1 rounded-full border-2 transition-all ${days === d ? 'bg-[#FFC8DD] border-[#D06892]' : 'bg-white border-black/15 opacity-70'}`}
              data-testid={`category-mix-range-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin opacity-60" />
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row items-center gap-4">
          {/* Donut chart */}
          <div
            className="relative w-40 h-40 shrink-0 rounded-full"
            style={{ background: gradient }}
            data-testid="category-mix-donut"
            role="img"
            aria-label="Kategori-mix donut-diagram"
          >
            <div className="absolute inset-[18%] rounded-full bg-white flex flex-col items-center justify-center text-center px-1">
              <span className="text-2xl font-bold leading-none">{slices.total}</span>
              <span className="text-[10px] opacity-60 font-bold uppercase tracking-wide">händelser</span>
            </div>
          </div>

          {/* Legend */}
          <div className="flex-1 min-w-0 w-full">
            {slices.items.length === 0 ? (
              <p className="text-sm opacity-60 text-center py-4">🌸 Inga kalender-händelser senaste {days} dagarna</p>
            ) : (
              <ul className="space-y-1.5" data-testid="category-mix-legend">
                {slices.items.map((s) => (
                  <li key={s.key} className="flex items-center gap-2" data-testid={`category-mix-row-${s.key}`}>
                    <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: s.meta.color }} />
                    <span className="shrink-0">{s.meta.emoji}</span>
                    <span className="text-sm font-bold flex-1 truncate">{s.meta.label}</span>
                    <span className="text-xs opacity-60 shrink-0">{s.count}</span>
                    <span className="text-xs font-bold w-10 text-right shrink-0">{Math.round(s.pct * 100)}%</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] opacity-70 mt-3 italic" data-testid="category-mix-insight">{insight}</p>
          </div>
        </div>
      )}
    </section>
  );
}
